import React, { useCallback, ReactNode } from 'react';
import { Address } from 'types';
import {
  useSmartyStreetsAutocomplete,
  useAddressValidation,
  useAddressStateMachine,
  ValidatedAddress,
} from 'hooks/address';
import { SingleLineAddressInputProvider } from './context';
import { TypeaheadInput } from './TypeaheadInput';
import { UnitField } from './UnitField';
import { ContinueButton } from './ContinueButton';

export interface SingleLineAddressInputProps {
  /**
   * SmartyStreets API key (website key for client-side use)
   */
  smartyStreetsAPIKey: string;

  /**
   * Callback when address input is complete and validated
   */
  onComplete?: (address: ValidatedAddress) => void;

  /**
   * Optional initial address value
   */
  initialAddress?: Address;

  /**
   * Custom children for compound component pattern
   */
  children?: ReactNode;

  /**
   * Class name for styling
   */
  className?: string;
}

/**
 * Main orchestrator component
 */
function SingleLineAddressInputRoot({
  smartyStreetsAPIKey: smartyApiKey,
  onComplete,
  initialAddress,
  children,
  className,
}: SingleLineAddressInputProps) {
  // Initialize hooks
  const autocomplete = useSmartyStreetsAutocomplete({
    apiKey: smartyApiKey,
    debounceMs: 300,
    maxSuggestions: 10,
  });

  const validation = useAddressValidation();

  const stateMachine = useAddressStateMachine({
    onComplete,
    onValidate: validation.validateAddress,
    initialAddress,
  });

  // Action handlers
  const handleSelectAddress = useCallback(
    (address: Address) => {
      autocomplete.clearSuggestions();
      stateMachine.transition({ type: 'SELECT_ADDRESS', payload: address });
    },
    [autocomplete, stateMachine]
  );

  const handleContinue = useCallback(() => {
    stateMachine.transition({
      type: 'CONTINUE',
    });
  }, [stateMachine]);

  const handleEnterUnit = useCallback(
    (unit: string) => {
      stateMachine.transition({ type: 'ENTER_UNIT', payload: unit });
    },
    [stateMachine]
  );

  // Context value
  const contextValue = {
    state: stateMachine.state,
    data: stateMachine.data,
    onSelectAddress: handleSelectAddress,
    onContinue: handleContinue,
    onReset: stateMachine.reset,
    onEnterUnit: handleEnterUnit,
    suggestions: autocomplete.suggestions,
    isLoadingSuggestions: autocomplete.isLoading,
    onSearchAddress: autocomplete.search,
    clearSuggestions: autocomplete.clearSuggestions,
    isValidating: validation.isValidating,
    smartyApiKey,
  };

  return (
    <SingleLineAddressInputProvider value={contextValue}>
      <div className={className}>{children}</div>
    </SingleLineAddressInputProvider>
  );
}

function DefaultComposition() {
  return (
    <>
      <TypeaheadInput />
      <UnitField />
      <ContinueButton />
    </>
  );
}

export const SingleLineAddressInput = Object.assign(
  SingleLineAddressInputRoot,
  {
    Typeahead: TypeaheadInput,
    UnitField: UnitField,
    ContinueButton: ContinueButton,
    Default: DefaultComposition,
  }
);
